/**
 * Onglet "Apps Mobiles" des paramètres — gestion du mini-store.
 * Permet à l'admin/directeur d'uploader des APK et de gérer leur visibilité.
 */
import { useEffect, useState } from 'react';
import {
  Smartphone, Plus, Upload, Edit2, Trash2, Loader2, X, Eye, EyeOff,
  Image as ImageIcon, ExternalLink, Globe2, Download, Users, TrendingUp,
} from 'lucide-react';

interface AppItem {
  id: number;
  slug: string;
  nom: string;
  description_courte: string;
  description: string;
  audience: string;
  audience_label: string;
  version: string;
  taille_humaine: string;
  min_android: string;
  notes_release: string;
  icone_url: string | null;
  apk_url: string | null;
  a_un_apk: boolean;
  publie: boolean;
  ordre_affichage: number;
  derniere_publication: string | null;
  nb_telechargements: number;
}

const AUDIENCE_OPTIONS = [
  { v: 'parent',    l: 'Parents et tuteurs' },
  { v: 'formateur', l: 'Formateurs / Enseignants' },
  { v: 'etudiant',  l: 'Étudiants' },
  { v: 'public',    l: 'Tous publics' },
];

function getToken(): string {
  return localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
}

async function apiFetch(url: string, options: RequestInit = {}): Promise<any> {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Authorization': `Bearer ${getToken()}`,
    },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try { detail = (await res.json()).detail || detail; } catch {/* ignore */}
    throw new Error(detail);
  }
  return res.json();
}

interface Stats {
  visites: { total: number; aujourd_hui: number; '7_jours': number; '30_jours': number; visiteurs_uniques_30j: number };
  telechargements: { total: number; aujourd_hui: number; '7_jours': number; '30_jours': number };
  par_app: Array<{ slug: string; nom: string; nb: number }>;
  serie_30j: Array<{ date: string; visites: number; telechargements: number }>;
}

export default function AppsMobilesTab() {
  const [apps, setApps] = useState<AppItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AppItem | 'new' | null>(null);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        apiFetch('/api/admin/apps/'),
        apiFetch('/api/admin/apps/stats/').catch(() => null),
      ]);
      setApps(r1.results || []);
      if (r2) setStats(r2);
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message || 'Erreur chargement' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (app: AppItem) => {
    if (!window.confirm(`Supprimer l'application « ${app.nom} » ?\n\nLe fichier APK et l'icône seront supprimés du serveur.`)) return;
    try {
      await apiFetch(`/api/admin/apps/${app.id}/`, { method: 'DELETE' });
      setMsg({ type: 'ok', text: `« ${app.nom} » supprimée.` });
      load();
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message });
    }
  };

  const togglePublie = async (app: AppItem) => {
    const fd = new FormData();
    fd.append('publie', String(!app.publie));
    try {
      await apiFetch(`/api/admin/apps/${app.id}/`, { method: 'PATCH', body: fd });
      load();
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message });
    }
  };

  return (
    <div className="space-y-5">
      {msg && (
        <div className={`p-3 rounded-lg text-sm ${
          msg.type === 'ok'
            ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700'
            : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700'
        }`}>{msg.text}</div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Smartphone size={20} /> Applications mobiles
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Publiez et gérez les APK téléchargeables depuis le mini-store public
            <a href="/apps" target="_blank" rel="noreferrer"
              className="ml-2 inline-flex items-center gap-1 text-blue-500 hover:text-blue-600 text-xs">
              <Globe2 size={11} /> /apps <ExternalLink size={10} />
            </a>
          </p>
        </div>
        <button onClick={() => setEditing('new')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus size={14} /> Nouvelle application
        </button>
      </div>

      {/* Stats analytics */}
      {stats && <BlocStats stats={stats} />}

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <Loader2 size={20} className="inline animate-spin" />
        </div>
      ) : apps.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-12 text-center">
          <Smartphone size={36} className="inline mb-2 opacity-40 text-gray-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Aucune application publiée pour l'instant.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Cliquez sur « Nouvelle application » pour publier votre premier APK.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {apps.map(app => (
            <article key={app.id}
              className={`bg-white dark:bg-gray-800 border rounded-xl p-4 ${
                app.publie
                  ? 'border-emerald-200 dark:border-emerald-700/50'
                  : 'border-gray-200 dark:border-gray-700 opacity-70'
              }`}>
              <div className="flex items-start gap-3 mb-3">
                {app.icone_url ? (
                  <img src={app.icone_url} alt=""
                    className="w-14 h-14 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 object-cover bg-gray-50 dark:bg-gray-900" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white">
                    <Smartphone size={22} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">{app.nom}</h3>
                  <div className="text-[10px] text-gray-500 truncate flex flex-wrap items-center gap-1.5 mt-0.5">
                    <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">{app.audience_label}</span>
                    {app.version && <span>v{app.version}</span>}
                    {app.taille_humaine && <span>· {app.taille_humaine}</span>}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    {app.nb_telechargements} téléchargement{app.nb_telechargements > 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {app.description_courte && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {app.description_courte}
                </p>
              )}

              <div className="flex flex-wrap gap-1 items-center">
                <button onClick={() => togglePublie(app)}
                  className={`px-2 py-1 text-[11px] rounded inline-flex items-center gap-1 ${
                    app.publie
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
                  }`}>
                  {app.publie ? <><Eye size={11} /> Publiée</> : <><EyeOff size={11} /> Brouillon</>}
                </button>
                {app.apk_url && (
                  <a href={app.apk_url} target="_blank" rel="noreferrer"
                    className="px-2 py-1 text-[11px] rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 inline-flex items-center gap-1">
                    <ExternalLink size={11} /> APK
                  </a>
                )}
                <div className="flex-1" />
                <button onClick={() => setEditing(app)}
                  className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
                  <Edit2 size={13} />
                </button>
                <button onClick={() => handleDelete(app)}
                  className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                  <Trash2 size={13} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <ModalAppForm
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); setMsg({ type: 'ok', text: 'Application enregistrée.' }); }}
        />
      )}
    </div>
  );
}

function BlocStats({ stats }: { stats: Stats }) {
  // Sparkline simple (SVG inline) sur les 30 derniers jours
  const serie = stats.serie_30j;
  const maxVal = Math.max(1, ...serie.flatMap(d => [d.visites, d.telechargements]));
  const W = 600, H = 80, padY = 8;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <TrendingUp size={12} /> Statistiques d'utilisation
      </h3>

      {/* Grille KPI 2 colonnes : Visites + Téléchargements */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-semibold text-blue-900 dark:text-blue-300 uppercase tracking-wide">Visites de /apps</span>
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-200">
            {stats.visites.total.toLocaleString('fr-FR')}
          </div>
          <div className="text-[11px] text-blue-700/80 dark:text-blue-400/80 mt-0.5">
            visites totales
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-3 text-[11px]">
            <Kpi label="Aujourd'hui" v={stats.visites.aujourd_hui} />
            <Kpi label="7 jours" v={stats.visites['7_jours']} />
            <Kpi label="30 jours" v={stats.visites['30_jours']} />
          </div>
          {stats.visites.visiteurs_uniques_30j > 0 && (
            <div className="text-[10px] text-blue-700/70 dark:text-blue-400/70 mt-1.5">
              {stats.visites.visiteurs_uniques_30j} visiteurs uniques (30 j)
            </div>
          )}
        </div>

        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Download size={14} className="text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-900 dark:text-emerald-300 uppercase tracking-wide">Téléchargements APK</span>
          </div>
          <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-200">
            {stats.telechargements.total.toLocaleString('fr-FR')}
          </div>
          <div className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 mt-0.5">
            téléchargements totaux
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-3 text-[11px]">
            <Kpi label="Aujourd'hui" v={stats.telechargements.aujourd_hui} />
            <Kpi label="7 jours" v={stats.telechargements['7_jours']} />
            <Kpi label="30 jours" v={stats.telechargements['30_jours']} />
          </div>
          {stats.par_app.length > 0 && (
            <div className="text-[10px] text-emerald-700/70 dark:text-emerald-400/70 mt-1.5 truncate">
              Top : {stats.par_app[0].nom} ({stats.par_app[0].nb})
            </div>
          )}
        </div>
      </div>

      {/* Courbe 30 jours */}
      {stats.serie_30j.some(d => d.visites > 0 || d.telechargements > 0) && (
        <div className="mt-4">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-0.5 bg-blue-500" /> Visites
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-0.5 bg-emerald-500" /> Téléchargements
            </span>
            <span className="ml-auto">30 derniers jours</span>
          </div>
          <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
            {/* Visites */}
            <polyline fill="none" stroke="#3b82f6" strokeWidth="1.5"
              points={serie.map((d, i) => {
                const x = (i / (serie.length - 1)) * W;
                const y = H - padY - (d.visites / maxVal) * (H - padY * 2);
                return `${x},${y}`;
              }).join(' ')} />
            {/* Téléchargements */}
            <polyline fill="none" stroke="#10b981" strokeWidth="1.5"
              points={serie.map((d, i) => {
                const x = (i / (serie.length - 1)) * W;
                const y = H - padY - (d.telechargements / maxVal) * (H - padY * 2);
                return `${x},${y}`;
              }).join(' ')} />
          </svg>
        </div>
      )}

      {/* Par appli */}
      {stats.par_app.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Téléchargements par application
          </div>
          <div className="space-y-1">
            {stats.par_app.slice(0, 5).map(a => {
              const max = stats.par_app[0].nb;
              const pct = max ? (a.nb / max) * 100 : 0;
              return (
                <div key={a.slug} className="flex items-center gap-2 text-xs">
                  <span className="flex-shrink-0 w-32 truncate text-gray-700 dark:text-gray-300">{a.nom}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="flex-shrink-0 font-semibold text-gray-700 dark:text-gray-300 w-10 text-right">{a.nb}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, v }: { label: string; v: number }) {
  return (
    <div className="text-center bg-white/50 dark:bg-black/20 rounded px-1.5 py-1">
      <div className="font-bold text-gray-900 dark:text-white">{v.toLocaleString('fr-FR')}</div>
      <div className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">{label}</div>
    </div>
  );
}

function ModalAppForm({ existing, onClose, onDone }: {
  existing: AppItem | null; onClose: () => void; onDone: () => void;
}) {
  const [slug, setSlug] = useState(existing?.slug || '');
  const [nom, setNom] = useState(existing?.nom || '');
  const [audience, setAudience] = useState(existing?.audience || 'parent');
  const [version, setVersion] = useState(existing?.version || '');
  const [minAndroid, setMinAndroid] = useState(existing?.min_android || '');
  const [descCourte, setDescCourte] = useState(existing?.description_courte || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [notes, setNotes] = useState(existing?.notes_release || '');
  const [publie, setPublie] = useState(existing?.publie ?? true);
  const [ordre, setOrdre] = useState(existing?.ordre_affichage || 0);
  const [apk, setApk] = useState<File | null>(null);
  const [icone, setIcone] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!slug || !nom) {
      setErreur('Slug et nom requis.'); return;
    }
    const fd = new FormData();
    if (!existing) fd.append('slug', slug);
    fd.append('nom', nom);
    fd.append('audience', audience);
    fd.append('version', version);
    fd.append('min_android', minAndroid);
    fd.append('description_courte', descCourte);
    fd.append('description', description);
    fd.append('notes_release', notes);
    fd.append('publie', String(publie));
    fd.append('ordre_affichage', String(ordre));
    if (apk) fd.append('apk', apk);
    if (icone) fd.append('icone', icone);

    setLoading(true);
    try {
      const url = existing
        ? `/api/admin/apps/${existing.id}/`
        : '/api/admin/apps/';
      await apiFetch(url, { method: existing ? 'PATCH' : 'POST', body: fd });
      onDone();
    } catch (err: any) {
      setErreur(err.message || 'Erreur enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 max-h-[92vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Smartphone size={18} /> {existing ? 'Modifier' : 'Nouvelle'} application
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Slug *</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder="parent, formateur…" required disabled={!!existing} maxLength={80}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 disabled:opacity-60" />
              <p className="text-[10px] text-gray-500 mt-0.5">Identifiant URL — non modifiable après création.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Audience *</label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)}
                className="w-full px-2 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
                {AUDIENCE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Nom affiché *</label>
            <input value={nom} onChange={(e) => setNom(e.target.value)} required maxLength={120}
              placeholder="Institut 2i — Parents"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Version</label>
              <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.2.0"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Android min</label>
              <input value={minAndroid} onChange={(e) => setMinAndroid(e.target.value)} placeholder="6.0"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Description courte</label>
            <input value={descCourte} onChange={(e) => setDescCourte(e.target.value)} maxLength={200}
              placeholder="Une phrase pour décrire l'app"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Description longue</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              rows={3} maxLength={5000}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Notes de version (optionnel)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2} maxLength={5000}
              placeholder="Nouveautés de cette version…"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 inline-flex items-center gap-1">
                <Upload size={11} /> Fichier APK {existing?.a_un_apk && <span className="text-[9px] text-emerald-600">(remplacer)</span>}
              </label>
              <input type="file" accept=".apk,application/vnd.android.package-archive"
                onChange={(e) => setApk(e.target.files?.[0] || null)}
                className="w-full text-xs text-gray-700 dark:text-gray-300 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-300" />
              <p className="text-[10px] text-gray-500 mt-0.5">Max 100 Mo</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 inline-flex items-center gap-1">
                <ImageIcon size={11} /> Icône (PNG/JPG)
              </label>
              <input type="file" accept="image/*"
                onChange={(e) => setIcone(e.target.files?.[0] || null)}
                className="w-full text-xs text-gray-700 dark:text-gray-300 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-amber-50 dark:file:bg-amber-900/30 file:text-amber-700 dark:file:text-amber-300" />
              <p className="text-[10px] text-gray-500 mt-0.5">Carrée, 512×512 idéal</p>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-900 rounded p-2.5">
            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={publie} onChange={(e) => setPublie(e.target.checked)}
                className="w-4 h-4 accent-blue-500" />
              Publiée (visible dans le store public)
            </label>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-700 dark:text-gray-300">Ordre</label>
              <input type="number" value={ordre} onChange={(e) => setOrdre(Number(e.target.value) || 0)}
                className="w-16 px-2 py-1 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
          </div>
          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded p-2 text-sm">
              {erreur}
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button type="submit" disabled={loading || !nom || !slug}
            className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {existing ? 'Enregistrer' : 'Créer'}
          </button>
        </footer>
      </form>
    </div>
  );
}
